import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, Users, TrendingUp, Shield, Sparkles, MessageCircle,
  CheckCircle2, ArrowRight, Star, Zap, Heart, Brain, Target,
  Award, Clock, BarChart3, UserCheck, Rocket
} from 'lucide-react';

export default function NewLandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Bilimsel Mizaç Analizi",
      description: "9 mizaç tipine göre kapsamlı değerlendirme ve kişiselleştirilmiş raporlar",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <MessageCircle className="w-8 h-8" />,
      title: "Kişisel AI Koç",
      description: "Mizaç tipinize özel yapay zeka destekli mentörlük ve rehberlik",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Hedef Belirleme",
      description: "Güçlü yönlerinize göre kariyer ve yaşam hedefleri oluşturma",
      color: "from-emerald-500 to-teal-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Aile Desteği",
      description: "Ebeveynler için çocuklarının mizaç profilini anlama ve rehberlik",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "İlerleme Takibi",
      description: "Gelişiminizi izleyin ve hedeflerinize ulaşın",
      color: "from-indigo-500 to-blue-500"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Sertifikalı Raporlar",
      description: "PDF formatında detaylı, bilimsel geçerliliği olan raporlar",
      color: "from-amber-500 to-yellow-500"
    }
  ];

  const steps = [
    {
      step: "01",
      title: "Kayıt Olun",
      description: "Ücretsiz hesap oluşturun ve rolünüzü seçin",
      icon: <UserCheck className="w-6 h-6" />
    },
    {
      step: "02",
      title: "Değerlendirme",
      description: "20 soruluk bilimsel mizaç envanterini tamamlayın",
      icon: <CheckCircle2 className="w-6 h-6" />
    },
    {
      step: "03",
      title: "Raporunuzu Alın",
      description: "AI destekli kişiselleştirilmiş mizaç raporunuzu inceleyin",
      icon: <Star className="w-6 h-6" />
    },
    {
      step: "04",
      title: "Koçunuzla Büyüyün",
      description: "Kişisel AI koçunuzla hedeflerinize ulaşın",
      icon: <Rocket className="w-6 h-6" />
    }
  ];

  const testimonials = [
    {
      name: "Ayşe Yılmaz",
      role: "Veli",
      content: "Çocuğumun mizaç özelliklerini anlamak, onunla iletişimimizi çok güçlendirdi. Artık ona daha iyi rehberlik edebiliyorum.",
      rating: 5,
      avatar: "👩"
    },
    {
      name: "Mehmet Demir",
      role: "Öğrenci",
      content: "Mizaç koçum sayesinde kendimi daha iyi tanıdım ve kariyer hedeflerimi netleştirdim. Harika bir deneyim!",
      rating: 5,
      avatar: "🧑"
    },
    {
      name: "Zeynep Kaya",
      role: "Öğretmen",
      content: "Sınıfımdaki öğrencilerin mizaç profillerini anlayarak, herkese uygun öğretim yöntemleri geliştirebildim.",
      rating: 5,
      avatar: "👩‍🏫"
    }
  ];

  const stats = [
    { number: "9", label: "Mizaç Tipi", icon: <Brain className="w-5 h-5" /> },
    { number: "1000+", label: "Mutlu Kullanıcı", icon: <Heart className="w-5 h-5" /> },
    { number: "%98", label: "Memnuniyet", icon: <Star className="w-5 h-5" /> },
    { number: "24/7", label: "AI Koç Desteği", icon: <MessageCircle className="w-5 h-5" /> }
  ];

  const faqs = [
    {
      question: "Mizaç nedir?",
      answer: "Mizaç, kişinin doğuştan gelen tepki ve davranış eğilimleridir. 9 temel mizaç tipi vardır ve her biri farklı güçlü yönler ve gelişim alanları içerir."
    },
    {
      question: "Mizaç değerlendirmesi ne kadar sürer?",
      answer: "Değerlendirme yaklaşık 10-15 dakika sürer. 20 soruluk envanter basit ve anlaşılırdır."
    },
    {
      question: "AI Koç nasıl çalışır?",
      answer: "AI Koçunuz, mizaç tipinize özel eğitilmiş yapay zeka destekli bir mentördür. Size özel rehberlik ve öneriler sunar."
    },
    {
      question: "Raporlar bilimsel mi?",
      answer: "Evet, raporlarımız bilimsel geçerliliği olan mizaç teorisi ve güncel araştırmalara dayanmaktadır."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header Navigation */}
      <header className="bg-white/80 backdrop-blur-md border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="/resources" className="text-slate-600 hover:text-blue-600 transition font-medium">Kaynaklar</a>
            <a href="/blog" className="text-slate-600 hover:text-blue-600 transition font-medium">Blog</a>
            <a href="/news" className="text-slate-600 hover:text-blue-600 transition font-medium">Haberler</a>
            <a href="/about" className="text-slate-600 hover:text-blue-600 transition font-medium">Hakkımızda</a>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/login')}
              className="text-xs text-slate-500 hover:text-purple-600"
            >
              <Shield className="w-3 h-3 mr-1" />
              Süper Admin
            </Button>
            <Button variant="outline" onClick={() => navigate('/login')}>Giriş Yap</Button>
            <Button className="bg-gradient-to-r from-blue-600 to-emerald-600" onClick={() => navigate('/register')}>
              Ücretsiz Başla
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <Badge className="mb-6 px-4 py-2 bg-gradient-to-r from-blue-100 to-emerald-100 text-blue-700 border-blue-200">
            <Sparkles className="w-4 h-4 mr-2 inline" />
            Türkiye'nin İlk AI Destekli Mizaç Platformu
          </Badge>
          
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Kendinizi Keşfedin,
            </span>
            <br />
            <span className="text-slate-800">Potansiyelinizi Ortaya Çıkarın</span>
          </h2>
          
          <p className="text-xl md:text-2xl text-slate-600 mb-10 max-w-3xl mx-auto leading-relaxed">
            Bilimsel mizaç analizi ve yapay zeka destekli kişisel koçluk ile 
            kendinizi daha iyi tanıyın, hedeflerinize ulaşın.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg px-10 py-7 rounded-2xl shadow-2xl hover:shadow-emerald-500/50 transition-all transform hover:scale-105 animate-pulse"
              onClick={() => navigate('/free-test')}
            >
              <Zap className="w-6 h-6 mr-2" />
              🎯 ÜCRETSİZ MİZAÇ TESTİ
            </Button>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-lg px-8 py-7 rounded-2xl shadow-2xl hover:shadow-blue-500/50 transition-all transform hover:scale-105"
              onClick={() => navigate('/register')}
            >
              <Rocket className="w-5 h-5 mr-2" />
              Hemen Başla
            </Button>
          </div>
          
          {/* Free Test Banner */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-6 max-w-2xl mx-auto mb-12 shadow-xl">
            <div className="flex items-center justify-center gap-4 text-white">
              <Sparkles className="w-8 h-8" />
              <div className="text-left">
                <p className="text-lg font-bold">İlk Mizaç Raporunuz Tamamen Ücretsiz!</p>
                <p className="text-sm opacity-90">Sadece 10 dakikada kendinizi keşfedin</p>
              </div>
              <Button 
                variant="secondary"
                className="bg-white text-emerald-600 hover:bg-emerald-50"
                onClick={() => navigate('/free-test')}
              >
                Başla →
              </Button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="bg-white/60 backdrop-blur-lg rounded-2xl p-4 shadow-lg border border-slate-200/50 hover:shadow-xl transition-all"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="text-blue-600">{stat.icon}</div>
                  <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                    {stat.number}
                  </p>
                </div>
                <p className="text-sm text-slate-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="py-16 px-6 bg-white border-y-2 border-blue-100">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-emerald-600 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
            <div className="text-center mb-8">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">Mizaç360 Nedir?</h3>
              <p className="text-lg md:text-xl text-blue-50 max-w-4xl mx-auto leading-relaxed">
                Mizaç360, bireylerin <strong>doğuştan gelen mizaç özelliklerini bilimsel yöntemlerle değerlendiren</strong>, 
                kişiselleştirilmiş rehberlik ve <strong>yapay zeka destekli koçluk</strong> hizmeti sunan Türkiye'nin 
                önde gelen eğitim teknolojisi platformudur.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Eğitimde Yenilik</h4>
                <p className="text-blue-100 text-sm">
                  Her öğrenciye özel öğrenme stratejileri geliştirerek akademik başarıyı artırır
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Bilimsel Temel</h4>
                <p className="text-blue-100 text-sm">
                  İsmail Acarkan, Ziya Selçuk, The Enneagram Institute gibi güvenilir kaynaklara dayanır
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2">Herkes İçin</h4>
                <p className="text-blue-100 text-sm">
                  Öğrenci, veli, öğretmen ve kurumlar için özel çözümler sunar
                </p>
              </div>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-blue-100 mb-4">
                <strong>Gizlilik & Güvenlik:</strong> Tüm verileriniz KVKK kapsamında korunur
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-blue-100 text-blue-700">Özellikler</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Neden Mizaç360?
            </h3>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Modern teknoloji ile geleneksel bilimi birleştiren kapsamlı çözümlerimiz
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-gradient-to-br from-white to-slate-50"
              >
                <CardHeader>
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-4 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-emerald-100 text-emerald-700">Süreç</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Nasıl Çalışır?
            </h3>
            <p className="text-xl text-slate-600">
              4 basit adımda kendinizi keşfedin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <Card className="border-none shadow-xl bg-white hover:shadow-2xl transition-all">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white shadow-lg">
                      {step.icon}
                    </div>
                    <div className="text-6xl font-bold text-slate-200 mb-2">{step.step}</div>
                    <CardTitle className="text-xl mb-2">{step.title}</CardTitle>
                    <CardDescription className="text-base">{step.description}</CardDescription>
                  </CardHeader>
                </Card>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-8 h-8 text-emerald-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-purple-100 text-purple-700">Kullanıcı Yorumları</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Kullanıcılarımız Ne Diyor?
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-xl bg-gradient-to-br from-white to-slate-50 hover:shadow-2xl transition-all">
                <CardHeader>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 italic mb-4">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{testimonial.avatar}</div>
                    <div>
                      <p className="font-semibold text-slate-800">{testimonial.name}</p>
                      <p className="text-sm text-slate-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 px-4 py-2 bg-blue-100 text-blue-700">SSS</Badge>
            <h3 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Sık Sorulan Sorular
            </h3>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-none shadow-lg bg-white">
                <CardHeader>
                  <CardTitle className="text-lg text-blue-700 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" />
                    {faq.question}
                  </CardTitle>
                  <CardDescription className="text-base text-slate-700 pl-7">
                    {faq.answer}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 via-emerald-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            Potansiyelinizi Keşfetmeye Hazır Mısınız?
          </h3>
          <p className="text-xl mb-10 opacity-90">
            Ücretsiz hesap oluşturun ve mizaç yolculuğunuza bugün başlayın
          </p>
          <Button 
            size="lg"
            className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-10 py-7 rounded-2xl shadow-2xl hover:shadow-white/50 transition-all transform hover:scale-105"
            onClick={() => navigate('/register')}
          >
            <Zap className="w-5 h-5 mr-2" />
            Ücretsiz Başla
          </Button>
        </div>
      </section>

      {/* Sources Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Kaynak ve Referanslar</Badge>
            <h2 className="text-4xl font-bold text-slate-800 mb-4">
              Bilimsel Temeller
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Mizaç360, alanında uzman isimlerin ve kurumların çalışmalarından yararlanmaktadır
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={() => window.open('https://www.amazon.com.tr/Enneagram-E%C5%9Fini-Ke%C5%9Ffet-%C4%B0smail-Acarkan/dp/6050845395', '_blank')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">İsmail Acarkan</h3>
                  <p className="text-sm text-slate-600">Enneagram Uzmanı</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Ziya Selçuk</h3>
                  <p className="text-sm text-slate-600">Eğitim Bilimci</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={() => window.open('https://www.eduen.com.tr', '_blank')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Eduen.com.tr</h3>
                  <p className="text-sm text-slate-600">Eğitim Platformu</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={() => window.open('https://www.enneagraminstitute.com', '_blank')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">The Enneagram Institute</h3>
                  <p className="text-sm text-slate-600">Uluslararası Otorite</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer" onClick={() => window.open('https://www.integrative9.com/enneagram/', '_blank')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-rose-600" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">Integrative9</h3>
                  <p className="text-sm text-slate-600">Araştırma Merkezi</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/about')}
              className="group"
            >
              Detaylı Bilgi ve Metodoloji
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">Mizaç360</span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Kendinizi keşfedin, potansiyelinizi ortaya çıkarın ve hedeflerinize ulaşın.
              </p>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Platform</h5>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="/pricing" className="hover:text-white transition">Fiyatlandırma</a></li>
                <li><a href="/about" className="hover:text-white transition">Hakkımızda</a></li>
                <li><a href="/about" className="hover:text-white transition">Metodoloji</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Destek</h5>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#" className="hover:text-white transition">Yardım Merkezi</a></li>
                <li><a href="#" className="hover:text-white transition">İletişim</a></li>
                <li><a href="#" className="hover:text-white transition">KVKK</a></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-semibold mb-4">Bilimsel Kaynaklar</h5>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="https://www.amazon.com.tr/Enneagram-E%C5%9Fini-Ke%C5%9Ffet-%C4%B0smail-Acarkan/dp/6050845395" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">İsmail Acarkan</a></li>
                <li>Ziya Selçuk</li>
                <li><a href="https://www.eduen.com.tr" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Eduen.com.tr</a></li>
                <li><a href="https://www.enneagraminstitute.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">The Enneagram Institute</a></li>
                <li><a href="https://www.integrative9.com/enneagram/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition">Integrative9</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8">
            <div className="flex flex-wrap justify-center gap-6 mb-6">
              <button 
                onClick={() => navigate('/kvkk')} 
                className="text-slate-400 hover:text-white text-sm transition"
              >
                KVKK Aydınlatma Metni
              </button>
              <button 
                onClick={() => navigate('/terms')} 
                className="text-slate-400 hover:text-white text-sm transition"
              >
                Kullanım Koşulları
              </button>
              <button 
                onClick={() => navigate('/privacy')} 
                className="text-slate-400 hover:text-white text-sm transition"
              >
                Gizlilik Politikası
              </button>
              <button 
                onClick={() => navigate('/cookies')} 
                className="text-slate-400 hover:text-white text-sm transition"
              >
                Çerez Politikası
              </button>
            </div>
            
            <div className="text-center">
              <p className="text-slate-400 text-sm">© 2025 Mizaç360. Tüm hakları saklıdır.</p>
              <p className="text-slate-500 text-xs mt-2">
                Bilimsel kaynaklar: İsmail Acarkan, Ziya Selçuk, Eduen.com.tr, The Enneagram Institute, Integrative9
              </p>
              <p className="text-slate-500 text-xs mt-3">
                <strong>Gizlilik:</strong> Kişisel verileriniz KVKK kapsamında korunmaktadır.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
}
